import React from 'react';
import { AlertTriangle, Plus, ArrowRight } from 'lucide-react';
import { Badge } from '../ui/badge';
import {
  ApprovalCard,
  ApprovalCardHeader,
  ApprovalCardTitle,
  ApprovalCardDescription,
  ApprovalCardContent,
  ApprovalButton,
} from '../shared/approval';
import type { PlaceholderStageReference } from './placeholderStageUtils';

interface PlaceholderStageWarningProps {
  placeholders: PlaceholderStageReference[];
  onCreateStage?: (stageName: string, placeholderStageId: string) => void;
  className?: string;
}

export const PlaceholderStageWarning: React.FC<PlaceholderStageWarningProps> = ({
  placeholders,
  onCreateStage,
  className = '',
}) => {
  if (placeholders.length === 0) return null;

  return (
    <ApprovalCard
      variant="outlined"
      className={`border-l-4 border-accent-warning-500 bg-accent-warning-500/5 ${className}`}
    >
      <ApprovalCardHeader>
        <div className="flex items-center space-x-2">
          <AlertTriangle className="w-5 h-5 text-accent-warning-500" />
          <ApprovalCardTitle className="text-accent-warning-600">
            Missing Stages Need to be Created
          </ApprovalCardTitle>
        </div>
        <ApprovalCardDescription>
          The following stages are referenced in your rules but don't exist yet. These are <strong>forward references</strong> - they won't appear in your stage list until you create them. You must create and configure these stages before activating this workflow.
        </ApprovalCardDescription>
      </ApprovalCardHeader>

      <ApprovalCardContent>
        <div className="space-y-4">
          {placeholders.map((placeholder, index) => (
            <div
              key={placeholder.referencedStageId}
              className="p-4 bg-card rounded-lg border border-accent-warning-500/30"
            >
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Badge variant="outline" className="bg-accent-warning-500/10 text-accent-warning-600 border-accent-warning-500/30">
                    Placeholder
                  </Badge>
                  <h4 className="font-semibold text-foreground">
                    {placeholder.referencedStageName}
                  </h4>
                </div>
                {onCreateStage && (
                  <ApprovalButton
                    variant="outline"
                    size="sm"
                    onClick={() => onCreateStage(placeholder.referencedStageName, placeholder.referencedStageId)}
                    className="flex items-center space-x-1"
                  >
                    <Plus className="w-3 h-3" />
                    <span>Create Stage</span>
                  </ApprovalButton>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm text-muted-foreground font-medium">
                  Referenced from:
                </p>
                <div className="space-y-1">
                  {placeholder.referencedFrom.map((ref, refIndex) => (
                    <div
                      key={`${ref.stageId}-${refIndex}`}
                      className="flex items-center space-x-2 text-sm"
                    >
                      <ArrowRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                      <span className="text-foreground font-medium">{ref.stageName}</span>
                      <span className="text-muted-foreground">
                        {ref.location === 'rule' && ref.ruleName && (
                          <>in rule "{ref.ruleName}"</>
                        )}
                        {ref.location === 'onApproved' && (
                          <>when approved</>
                        )}
                        {ref.location === 'onRejected' && (
                          <>when rejected</>
                        )}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </ApprovalCardContent>
    </ApprovalCard>
  );
};
